import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { MapPin, DollarSign, Briefcase, Eye, Clock, Shield, Users } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';

const MyApplicationsTable = ({ applications }) => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const [visibleCount, setVisibleCount] = useState(10);
  
  const currentLang = i18n.language || 'en';
  const isCebuano = currentLang === 'ceb';
  
  const getLocalizedField = (job, field) => {
    if (isCebuano && job?.[`${field}_ceb`]) {
      return job[`${field}_ceb`];
    }
    return job?.[field];
  };

  // Sort applications by created_at DESC (newest first)
  const sortedApplications = [...applications].sort((a, b) => 
    new Date(b.created_at) - new Date(a.created_at)
  );

  const visibleApplications = sortedApplications.slice(0, visibleCount);
  const hasMore = sortedApplications.length > visibleCount;

  const handleLoadMore = () => {
    setVisibleCount(prev => prev + 10);
  };

  const getStatusVariant = (status) => {
    switch (status) {
      case 'hired':
        return 'default';
      case 'shortlisted':
        return 'default';
      case 'rejected':
        return 'secondary';
      case 'viewed':
        return 'secondary';
      default:
        return 'outline';
    }
  };

  const getStatusLabel = (status, t) => {
    switch (status) {
      case 'hired':
        return `✓ ${t('tables.hired')}`;
      case 'shortlisted':
        return `✓ ${t('tables.movingForward', 'Moving forward')}`;
      case 'rejected':
        return t('tables.notSelected', 'Not selected this time');
      case 'viewed':
        return t('tables.viewed');
      case 'applied':
      case 'pending':
        return t('tables.pending');
      default:
        return status;
    }
  };

  const formatViewedTime = (viewedAt) => {
    if (!viewedAt) return null;
    try {
      return formatDistanceToNow(new Date(viewedAt), { addSuffix: true });
    } catch (error) {
      return null;
    }
  };

  return (
    <div className="space-y-4">
      <Card>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="border-b bg-muted/30">
              <tr>
                <th className="py-3 px-4 text-left font-semibold text-sm">{t('tables.jobTitle')}</th>
                <th className="py-3 px-4 text-left font-semibold text-sm hidden lg:table-cell">{t('tables.employer')}</th>
                <th className="py-3 px-4 text-left font-semibold text-sm hidden md:table-cell">{t('tables.location')}</th>
                <th className="py-3 px-4 text-left font-semibold text-sm hidden sm:table-cell">{t('tables.salary')}</th>
                <th className="py-3 px-4 text-left font-semibold text-sm">{t('tables.status')}</th>
                <th className="py-3 px-4 text-left font-semibold text-sm hidden xl:table-cell">{t('tables.viewed')}</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {visibleApplications.map((app) => {
                const job = app.job || {};
                const employer = job.employer || {};
                const viewedTime = formatViewedTime(app.viewed_at);

                return (
                  <tr 
                    key={app.id} 
                    className="hover:bg-muted/50 transition-colors cursor-pointer"
                    onClick={() => navigate(`/jobs/${job.id}`)}
                    data-testid={`application-row-${app.id}`}
                  >
                    <td className="py-3 px-4">
                      <div>
                        <div className="font-medium mb-1">{getLocalizedField(job, 'title') || t('tables.jobTitle')}</div>
                        {/* Mobile: Show employer and location */}
                        <div className="flex flex-col gap-1 text-xs text-muted-foreground lg:hidden">
                          <div className="flex items-center gap-1">
                            <Briefcase size={12} />
                            <span>{employer.business_name || employer.name || 'Employer'}</span>
                          </div>
                          <div className="flex items-center gap-1">
                            <MapPin size={12} />
                            <span>{job.location || 'Location'}</span>
                          </div>
                        </div>

                        {/* Quick Filter Badges */}
                        <div className="flex flex-wrap gap-1 mt-1">
                          {job.positions_available > 1 && (
                            <span className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full bg-purple-50 text-purple-700 border border-purple-200 font-medium dark:bg-purple-950 dark:text-purple-300 dark:border-purple-800">
                              <Users size={10} /> Hiring {job.positions_available}
                            </span>
                          )}
                          {job.urgent_hiring && (
                            <span className="text-[11px] px-2 py-0.5 rounded-full bg-red-50 text-red-700 border border-red-200 font-medium">
                              🔴 Urgent
                            </span>
                          )}
                          {job.no_experience_needed && (
                            <span className="text-[11px] px-2 py-0.5 rounded-full bg-green-50 text-green-700 border border-green-200 font-medium">
                              ⭐ No Exp
                            </span>
                          )}
                          {job.with_housing && (
                            <span className="text-[11px] px-2 py-0.5 rounded-full bg-blue-50 text-blue-700 border border-blue-200 font-medium">
                              🏠 Housing
                            </span>
                          )}
                        </div>

                        {/* Show viewed status on mobile under job title */}
                        {viewedTime && (
                          <div className="flex items-center gap-1 text-xs text-primary mt-1 xl:hidden">
                            <Eye size={12} />
                            <span>{t('tables.viewed')} {viewedTime}</span>
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="py-3 px-4 text-sm hidden lg:table-cell">
                      <div className="flex items-center gap-2">
                        {employer.verified && (
                          <Shield size={14} className="text-teal-600 fill-teal-600 flex-shrink-0" title="Verified True Boholano" />
                        )}
                        <span>{employer.business_name || employer.name || 'Employer'}</span>
                      </div>
                    </td>
                    <td className="py-3 px-4 text-sm text-muted-foreground hidden md:table-cell">
                      <div className="flex items-center gap-1">
                        <MapPin size={14} />
                        {job.location || '-'}
                      </div>
                    </td>
                    <td className="py-3 px-4 text-sm text-muted-foreground hidden sm:table-cell">
                      {job.salary_min && job.salary_max ? (
                        <div className="flex items-center gap-1">
                          <span className="font-bold">₱</span>
                          <span>{job.salary_min.toLocaleString()} - ₱{job.salary_max.toLocaleString()}</span>
                        </div>
                      ) : '-'}
                    </td>
                    <td className="py-3 px-4">
                      <Badge variant={getStatusVariant(app.status)} className="text-xs">
                        {getStatusLabel(app.status, t)}
                      </Badge>
                    </td>
                    <td className="py-3 px-4 hidden xl:table-cell">
                      {viewedTime ? (
                        <div className="flex items-center gap-1 text-sm text-primary">
                          <Eye size={14} />
                          <span>{viewedTime}</span>
                        </div>
                      ) : (
                        <div className="flex items-center gap-1 text-sm text-muted-foreground">
                          <Clock size={14} />
                          <span>{t('tables.notViewed')}</span>
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>

      {hasMore && (
        <div className="flex justify-center">
          <Button
            variant="outline"
            onClick={handleLoadMore}
            className="rounded-xl"
            data-testid="load-more-applications"
          >
            {t('tables.loadMore')} ({sortedApplications.length - visibleCount} {t('tables.remaining')})
          </Button>
        </div>
      )}
    </div>
  );
};

export default MyApplicationsTable;
